// src/store/slices/comment.slice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { commentController } from '../../controllers/comment.controller'

/* ---------------------------------------------------------- */
/* ------------------ Async Thunks -------------------------- */
/* ---------------------------------------------------------- */

// 🔹 שליפת תגובות לפי פוסט
export const fetchComments = createAsyncThunk(
  'comment/fetchComments',
  async (postId, { rejectWithValue }) => {
    try {
      return await commentController.fetchComments(postId)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// 🔹 שליפת תגובות לפי מזהה משתמש
export const fetchCommentsByUserId = createAsyncThunk(
  'comment/fetchCommentsByUserId',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error('User ID is required')
      const comments = await commentController.fetchCommentsByUserId(userId)
      return comments
    } catch (err) {
      console.error('Error in fetchCommentsByUserId:', err)
      return rejectWithValue(err.message)
    }
  }
)

// 🔹 הוספת תגובה
export const addComment = createAsyncThunk(
  'comment/addComment',
  async ({ commentData, loggedInUser }, { rejectWithValue }) => {
    try {
      return await commentController.addComment(commentData, loggedInUser)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// 🔹 עריכת תגובה
export const editComment = createAsyncThunk(
  'comment/editComment',
  async ({ commentId, updatedData, loggedInUser }, { rejectWithValue }) => {
    try {
      return await commentController.editComment(
        commentId,
        updatedData,
        loggedInUser
      )
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// 🔹 מחיקת תגובה
export const removeComment = createAsyncThunk(
  'comment/removeComment',
  async ({ commentId, loggedInUser }, { rejectWithValue }) => {
    try {
      await commentController.removeComment(commentId, loggedInUser)
      return commentId
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// 🔹 לייק/אנלייק על תגובה
export const toggleLikeComment = createAsyncThunk(
  'comment/toggleLike',
  async ({ commentId, loggedInUser }, { rejectWithValue }) => {
    try {
      return await commentController.toggleLike(commentId, loggedInUser)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

/* ---------------------------------------------------------- */
/* ---------------------- Slice ----------------------------- */
/* ---------------------------------------------------------- */

const initialState = {
  comments: [], // רשימת התגובות
  isLoading: false, // מצב טעינה
  error: null, // מצב שגיאה
}

const commentSlice = createSlice({
  name: 'comment',
  initialState,
  reducers: {
    addCommentDirect(state, action) {
      state.comments.push(action.payload)
    },
    updateCommentDirect(state, action) {
      const idx = state.comments.findIndex(
        (comment) => comment._id === action.payload._id
      )
      if (idx !== -1) {
        state.comments[idx] = action.payload
      }
    },
    removeCommentDirect(state, action) {
      state.comments = state.comments.filter(
        (comment) => comment._id !== action.payload
      )
    },
  },
  extraReducers: (builder) => {
    builder
      /* 🔹 שליפת תגובות לפי פוסט */
      .addCase(fetchComments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.isLoading = false
        state.comments = action.payload
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      /* 🔹 שליפת תגובות לפי משתמש */
      .addCase(fetchCommentsByUserId.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCommentsByUserId.fulfilled, (state, action) => {
        state.isLoading = false
        state.comments = action.payload
      })
      .addCase(fetchCommentsByUserId.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      /* 🔹 הוספת תגובה */
      .addCase(addComment.fulfilled, (state, action) => {
        const newComment = action.payload
        const commentExists = state.comments.some(
          (comment) => comment._id === newComment._id
        )
        if (!commentExists) {
          state.comments.push(newComment)
        }
      })

      /* 🔹 עריכת תגובה */
      .addCase(editComment.fulfilled, (state, action) => {
        const idx = state.comments.findIndex(
          (comment) => comment._id === action.payload._id
        )
        if (idx !== -1) {
          state.comments[idx] = action.payload
        }
      })

      /* 🔹 מחיקת תגובה */
      .addCase(removeComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(
          (comment) => comment._id !== action.payload
        )
      })

      /* 🔹 לייק/אנלייק על תגובה */
      .addCase(toggleLikeComment.fulfilled, (state, action) => {
        const updatedComment = action.payload
        const idx = state.comments.findIndex(
          (comment) => comment._id === updatedComment._id
        )
        if (idx !== -1) {
          state.comments[idx] = updatedComment
        }
      })
  },
})

/* ---------------------------------------------------------- */
/* -------------------- Exports ----------------------------- */
/* ---------------------------------------------------------- */

export const {
  addCommentDirect,
  updateCommentDirect,
  removeCommentDirect,
} = commentSlice.actions

export const commentReducer = commentSlice.reducer
